import React, { useState } from 'react';
import { searchAPI } from '../services/api';

const statusClasses = {
  available: 'bg-emerald-100 text-emerald-700',
  checked_out: 'bg-blue-100 text-blue-700',
  overdue: 'bg-red-100 text-red-700',
  maintenance: 'bg-amber-100 text-amber-700',
  calibration: 'bg-orange-100 text-orange-700',
  retired: 'bg-slate-100 text-slate-600',
  active: 'bg-blue-100 text-blue-700',
  returned: 'bg-emerald-100 text-emerald-700',
};
const Badge = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${statusClasses[status] || 'bg-slate-100 text-slate-600'}`}>{status}</span>
);

const SectionTable = ({ title, icon, children, count }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
    <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-100">
      <span className="text-lg">{icon}</span>
      <h4 className="font-semibold text-slate-900">{title}</h4>
      <span className="ml-auto px-2.5 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-full">{count}</span>
    </div>
    <div className="overflow-x-auto">{children}</div>
  </div>
);

const Th = ({ children }) => (
  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">{children}</th>
);

const SearchPage = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchType, setSearchType] = useState('all');

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    try {
      const response = await searchAPI.search(query, searchType);
      setResults(response.data.results);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const hasResults = results && (
    (results.tools && results.tools.length > 0) ||
    (results.employees && results.employees.length > 0) ||
    (results.transactions && results.transactions.length > 0)
  );

  return (
    <div className="space-y-6">
      {/* Search Bar */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[200px] relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input type="text" placeholder="Search for tools, employees, transactions..."
              className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              value={query} onChange={e => setQuery(e.target.value)} />
          </div>
          <select value={searchType} onChange={e => setSearchType(e.target.value)}
            className="px-3 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white">
            <option value="all">All Types</option>
            <option value="tools">Tools</option>
            <option value="employees">Employees</option>
            <option value="transactions">Transactions</option>
          </select>
          <button type="submit" disabled={loading}
            className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            {loading ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                Searching...
              </>
            ) : 'Search'}
          </button>
        </form>
      </div>

      {/* Results */}
      {results && !hasResults && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 flex flex-col items-center gap-3">
          <svg className="w-14 h-14 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p className="font-semibold text-slate-500">No results found</p>
          <p className="text-sm text-slate-400">Try different search terms</p>
        </div>
      )}

      {results?.tools && results.tools.length > 0 && (
        <SectionTable title="Tools" icon="🔧" count={results.tools.length}>
          <table className="w-full">
            <thead><tr><Th>Tool Name</Th><Th>Serial Number</Th><Th>Category</Th><Th>Status</Th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {results.tools.map(tool => (
                <tr key={tool.tool_id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 text-sm font-semibold text-slate-900">{tool.tool_name}</td>
                  <td className="px-4 py-3"><code className="text-xs bg-slate-100 px-2 py-0.5 rounded">{tool.serial_number}</code></td>
                  <td className="px-4 py-3 text-sm text-slate-700">{tool.category}</td>
                  <td className="px-4 py-3"><Badge status={tool.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionTable>
      )}

      {results?.employees && results.employees.length > 0 && (
        <SectionTable title="Employees" icon="👥" count={results.employees.length}>
          <table className="w-full">
            <thead><tr><Th>Name</Th><Th>Employee Code</Th><Th>Department</Th><Th>Role</Th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {results.employees.map(emp => (
                <tr key={emp.employee_id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 text-sm font-semibold text-slate-900">{emp.first_name} {emp.last_name}</td>
                  <td className="px-4 py-3"><code className="text-xs bg-slate-100 px-2 py-0.5 rounded">{emp.employee_code}</code></td>
                  <td className="px-4 py-3 text-sm text-slate-700">{emp.department}</td>
                  <td className="px-4 py-3 text-sm text-slate-700 capitalize">{emp.role}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionTable>
      )}

      {results?.transactions && results.transactions.length > 0 && (
        <SectionTable title="Transactions" icon="📋" count={results.transactions.length}>
          <table className="w-full">
            <thead><tr><Th>Tool</Th><Th>Employee</Th><Th>Date</Th><Th>Status</Th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {results.transactions.map(tx => (
                <tr key={tx.transaction_id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 text-sm font-semibold text-slate-900">{tx.tool_name}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{tx.first_name} {tx.last_name}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{new Date(tx.checkout_date).toLocaleDateString()}</td>
                  <td className="px-4 py-3"><Badge status={tx.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionTable>
      )}
    </div>
  );
};

export default SearchPage;
